import { z } from 'zod';
import { RANK_GROUP_KEYS } from '../constants/rankGroup.constant';
import { SLUG_REGEX, toSlug } from '../utils/slug.util';
import { createPaginationSchema, objectIdSchema } from './common.schema';
import { MASTER_LIFECYCLE_STATUSES, archivableFieldsSchema, showLifecycleSchema } from './lifecycle.schema';
import { postTypeSummarySchema } from './postType.schema';
import { vehicleTypeSummarySchema } from './vehicleType.schema';

/**
 * Policies and their versions (PA_REQUIREMENTS FR-5.1.4, §4.2).
 *
 * A policy is a COLUMN-SET TEMPLATE: an ordered selection of post types, a vehicle
 * block, and a set of command roles. It describes a shape and holds no people — P3's
 * intake wizard is what fills it in.
 *
 * ⚠️ PUBLISHING FREEZES A VERSION, FOR EVER.
 *
 * §3's second principle: "editing a policy next month must never rewrite last month's
 * order". An arrangement snapshots a `policyVersionId`, never a `policyId`, so a
 * published version is immutable in the byte-comparison sense. Editing one creates
 * version n+1 and leaves n untouched — `api/src/services/policy.service.ts` has the
 * algorithm and `api/tests/integration/policyVersioning.test.ts` proves the freeze.
 *
 * ⚠️ NOTHING HERE IS RECURSIVE, so no `z.lazy` and no hand-written DTO.
 * `orgUnit.schema.ts` explains why the org TREE cannot be a recursive DTO under
 * CLAUDE.md §6, and that reasoning looks like it applies here. It does not:
 * `postTypes`, `vehicleTypes` and `commandRoles` are each ONE level of
 * `z.array(z.object({…}))`, and every DTO below stays a plain `z.infer`.
 */

export const POLICY_VERSION_STATUSES = ['DRAFT', 'PUBLISHED'] as const;
export type PolicyVersionStatus = (typeof POLICY_VERSION_STATUSES)[number];

/**
 * ⚠️ [amended D4] WHO MADE THIS VERSION, AND IT DECIDES WHETHER IT BECOMES THE CURRENT ONE.
 *
 * D4 §5c lets a nodal change a duty point's column SET, which forks a new version and
 * re-points that duty point at it. That version is created PUBLISHED — it is in use the
 * moment the duty point references it, and a DRAFT would collide with the one-draft partial
 * unique index — so `syncPolicyPointers`, which picks the highest PUBLISHED version, would
 * by construction make it the policy's CURRENT one for everybody.
 *
 * That is the wrong blast radius. `policy.write` is held by SUPER_ADMIN and — since [C5c §3],
 * behind a service seat guard — a NODAL_ADMIN seated at the commissionerate root, precisely
 * because "a policy change reshapes every future arrangement across the whole commissionerate"
 * (CLAUDE.md §10); an inline tweak by a divisional nodal holding `arrangement.write` must not
 * re-point what every other operator is offered by default. A divisional nodal is still not a
 * policy author, so this argument is unchanged. So `syncPolicyPointers` filters on
 * `origin: 'POLICY'`, and an arrangement-derived version is real, referenced, listed and
 * frozen — but never current.
 *
 * ⚠️ `default: 'POLICY'` ON THE MODEL, so every version written before D4 hydrates and
 * validates on a title-only save. The same argument `occurrences` makes against `required`.
 */
export const POLICY_VERSION_ORIGINS = ['POLICY', 'ARRANGEMENT'] as const;
export type PolicyVersionOrigin = (typeof POLICY_VERSION_ORIGINS)[number];

// ── Command roles ────────────────────────────────────────────────────────────────

/**
 * ⚠️ COMMAND ROLES ARE DEFINED PER POLICY VERSION. THERE IS NO CommandRole COLLECTION.
 *
 * Supervision, In Charge and Assisting Inspector are the standard set, but a Durga Puja
 * or Republic Day policy may declare an entirely different one. The mitigation for that
 * freedom is "copy command roles from an existing policy" on creation
 * (`copyCommandRolesFromPolicyId` below) — divergence is possible, sameness is the easy
 * path.
 *
 * ⚠️ THIS DEVIATES FROM PA_REQUIREMENTS FR-5.2.5 AND OD-18, which both describe a global
 * master table. The deviation is deliberate and is recorded in the P2 CHANGELOG. What
 * FR-5.2.5 actually wanted — "a third role can be added without a schema change" —
 * still holds; what is given up is renaming a role across every policy in one edit.
 *
 * ⚠️ AND THEY SIT OUTSIDE THE REQUIREMENT COUNTS ENTIRELY.
 *
 * No count, no allocation, no fulfilment percentage, no shortfall. FR-5.2.6b closes OD-9
 * precisely because everybody assumes command counts toward force. A PA fills each role
 * with zero or more named officers and that is all. `commandRoleIsolation.test.ts` holds
 * the line mechanically, because a comment cannot.
 */

/**
 * INPUT. `key` absent means a NEW role and the server mints one; `key` present means
 * carry this existing role forward under a possibly-edited label.
 */
export const commandRoleInputSchema = z.object({
  key: z.string().trim().min(1).max(40).regex(SLUG_REGEX).optional(),
  label: z
    .string()
    .trim()
    .min(1, 'A command role needs a label')
    .max(80)
    // A label that slugs to nothing cannot be given a key, and a role with no key cannot
    // be referred to across versions. Caught here rather than as a 500 at minting time.
    .refine((label) => toSlug(label).length > 0, 'A command role label needs at least one letter or digit'),
  isRequired: z.boolean().default(false),
  /**
   * ⚠️ [P4-UX.2] WHICH RANKS MAY HOLD THIS ROLE. OPTIONAL, AND EMPTY MEANS ANYONE.
   *
   * "In Charge may only be an Inspector or above" is a real rule in some policies and no
   * rule at all in others, so it is a per-role allow-list rather than a new master. Empty
   * is the default and means exactly what it says: no restriction, anybody may be picked.
   *
   * ⚠️ IT IS AN ELIGIBILITY FILTER, NOT A QUANTITY, AND THAT KEEPS FR-5.2.6b INTACT.
   * Nothing counts it, allocates against it, or balances it — `post_types` have carried
   * the same kind of rank eligibility since P2. `commandRoleIsolation.test.ts`'s CLAIM 3
   * key list is widened for it deliberately, with the reason recorded there.
   */
  eligibleRankIds: z.array(objectIdSchema).max(40).default([]),
  /**
   * ─── ⚠️ [C2b §5c] GROUP REFERENCES, BESIDE THE INDIVIDUAL CODES AND NEVER INSTEAD OF THEM ──
   *
   *  A command role's allowed ranks is a MIXED list. `{group: AC}` means any Assistant
   *  Commissioner INCLUDING ONE ADDED NEXT YEAR; `{rank: 'AC(W)'}` means that rank alone, for
   *  ever. Storing five ids and calling it a group loses that distinction permanently.
   *
   *  ⚠️ TWO NAMED COLUMNS, NEVER ONE DISCRIMINATED ARRAY. [P6b]'s rule, and here it also buys
   *  §5f for free: an existing policy's stored ids are UNTOUCHED and are never re-read as a
   *  group. Nothing infers that five AC variants "meant" the group.
   *
   *  ⚠️ KEYED ON THE GROUP KEY, NOT ON RANK IDS. That is the whole point — an id list would be
   *  a snapshot of today's master, which is the thing a group exists not to be.
   *
   *  ⚠️ AND THE EXPANSION IS ONE FUNCTION. `expandRankSelection` is called by the OFFERED set
   *  (the officer picker's `rankIds` query) and by the ACCEPTED set
   *  (`assertCommandOfficersEligible`), so the search and the refusal cannot disagree —
   *  `eligibleRankIdsFor`'s precedent, and its stated property: neither call site changes.
   */
  eligibleRankGroupKeys: z.array(z.enum(RANK_GROUP_KEYS)).max(RANK_GROUP_KEYS.length).default([]),
});

/** OUTPUT. `key` is stable for ever; `label` is free; `order` is the array index. */
export const commandRoleSchema = z.object({
  /**
   * ⚠️ GENERATED ONCE, FROM THE FIRST LABEL, AND NEVER RECOMPUTED.
   *
   * Editing a label must not change the key — without that, "In Charge" in one policy and
   * "Incharge" in another are different things for ever and cross-policy reporting on who
   * commanded what becomes guesswork. The key travels on the wire on every edit, which is
   * what makes "never recomputed" mechanical rather than a promise.
   */
  key: z.string(),
  label: z.string(),
  /** The position, assigned by the server from the array index. Not a quantity. */
  order: z.number().int().min(0),
  /**
   * ⚠️ AN INTAKE-FORM HINT, ENFORCED IN THE STANDARD PA FORM AND NOWHERE ELSE.
   *
   * The model never rejects a PA for missing command, because round-the-clock
   * arrangements draw from this same policy pool and deliberately capture no supervision
   * at all. It does not gate publishing either.
   */
  isRequired: z.boolean(),
  /**
   * ⚠️ [P4-UX.2] WHICH RANKS MAY HOLD THIS ROLE. OPTIONAL, AND EMPTY MEANS ANYONE.
   *
   * "In Charge may only be an Inspector or above" is a real rule in some policies and no
   * rule at all in others, so it is a per-role allow-list rather than a new master. Empty
   * is the default and means exactly what it says: no restriction, anybody may be picked.
   *
   * ⚠️ IT IS AN ELIGIBILITY FILTER, NOT A QUANTITY, AND THAT KEEPS FR-5.2.6b INTACT.
   * Nothing counts it, allocates against it, or balances it — `post_types` have carried
   * the same kind of rank eligibility since P2. `commandRoleIsolation.test.ts`'s CLAIM 3
   * key list is widened for it deliberately, with the reason recorded there.
   */
  eligibleRankIds: z.array(objectIdSchema),
  /**
   * ⚠️ [C2b §5c] THE RAW PAIR REACHES THE CLIENT, NEVER THE EXPANDED UNION.
   *
   * A pre-expanded `eligibleRankIds` would show five AC ids and no group when the role is
   * opened to EDIT — §5c's whole distinction lost on a round trip, and §5e's chips unable to
   * tell a group from a rank. The picker expands with the same shared function the server does.
   */
  eligibleRankGroupKeys: z.array(z.enum(RANK_GROUP_KEYS)),
});

// ── Policy version content ───────────────────────────────────────────────────────

const uniqueIds = (ids: string[]): boolean => new Set(ids).size === ids.length;

/**
 * ⚠️ ORDER IS THE ARRAY POSITION. `displayOrder` IS NEVER ACCEPTED ON INPUT.
 *
 * Two sources of truth for ordering is how a list renders in an order nobody chose — and
 * they diverge the first time a row is removed. The server assigns `displayOrder` (and
 * `order`, for command roles) from the index, and the response carries it back so a
 * client cannot get it wrong.
 */
export const policyVersionContentSchema = z.object({
  postTypes: z.array(objectIdSchema).max(30).refine(uniqueIds, 'A post type may appear only once'),
  vehicleTypes: z.array(objectIdSchema).max(10).refine(uniqueIds, 'A vehicle type may appear only once'),
  commandRoles: z
    .array(commandRoleInputSchema)
    .max(10)
    .refine(
      (rows) => new Set(rows.map((row) => row.label.trim().toLowerCase())).size === rows.length,
      'Two command roles cannot share a label',
    ),
  effectiveFrom: z.iso.datetime().optional(),
});

/**
 * A draft may be EMPTY. `postTypes` is required non-empty at PUBLISH, checked in the
 * service — it is a state-transition rule, not a shape rule, and a half-built draft has
 * to stay saveable.
 */
/**
 * ⚠️ [LIFECYCLE] THE THREE `.default([])`s ARE GONE, AND THAT FIXES A REAL DEFECT.
 *
 * They made "omitted" and "explicitly empty" INDISTINGUISHABLE at the service, which read
 * `input.postTypes.length > 0` and therefore treated `[]` as "say nothing, clone the
 * source". The consequence, reported from the field as "a vehicle type cannot be
 * unselected": `POST /policies/:id/versions` with `{ fromVersionId, vehicleTypes: [] }`
 * carried the source's vehicle types forward, so no draft could ever clear the block.
 *
 * It also made §6f unbuildable. The cascade offers "create a draft of this policy WITHOUT
 * the archived column"; for a policy whose only post column was the archived one, the
 * correct payload is `postTypes: []` — which silently cloned the archived column straight
 * back into the new draft.
 *
 * Without the defaults, `.partial()` leaves them plain optional and the service can ask
 * `!== undefined`, which is exactly what the PATCH path has always done correctly. This is
 * CLAUDE.md §6's own rule arriving one level down: a `.partial()`-ed base carries no
 * `.default()`, because the default defeats the distinction the partial exists to express.
 */
export const createPolicyVersionSchema = policyVersionContentSchema.partial().extend({
  /** Clone an existing version's contents into the new draft rather than starting empty. */
  fromVersionId: objectIdSchema.optional(),
});

export const updatePolicyVersionSchema = policyVersionContentSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: 'At least one field must be provided' });

// ── Policy ───────────────────────────────────────────────────────────────────────

export const policyBaseSchema = z.object({
  name: z.string().trim().min(1, 'Policy name is required').max(160),
  /**
   * ⚠️ [LIFECYCLE] `.min(1)` MADE A DESCRIPTION SECRETLY MANDATORY, AND NOTHING SAID SO.
   *
   * `useZodForm`'s `zodRule` validates by parsing the WHOLE form and filtering the issues
   * to this field's path, and `PolicyBuilder` seeds `description: ''`. So `''` failed
   * `.min(1)`, `validateFields()` failed, and **`onFinish` never ran** — a policy could not
   * be created at all without typing a description.
   *
   * ⚠️ AND THE ASTERISK DISAGREED WITH THE VALIDATION, which is precisely the failure
   * `lib/zodForm.ts`'s header says `isFieldRequired` exists to prevent: it probes with the
   * key ABSENT, where `.optional()` passes, so the field rendered as optional while
   * refusing to submit. The red border had no sentence beside it, because a whole-form
   * refusal has nowhere to put one.
   *
   * Accepting `''` also makes a description CLEARABLE, which it never was: `updatePolicySchema`
   * rejected the empty string and omitting the key means "no change".
   */
  description: z.string().trim().max(1_000).optional(),
});

export const createPolicySchema = policyBaseSchema
  .extend({
    isDefault: z.boolean().default(false),
    /**
     * ⚠️ COPY THE KEYS, NOT THE LABELS — which is why this is a server-side field and
     * not a web convenience.
     *
     * Reading the source policy server-side copies each role's `key` verbatim. Posting
     * the source's LABELS back would re-mint them, so a role whose label was edited last
     * month would arrive under a key that no longer matches it, and the two policies'
     * command roles would silently stop being comparable.
     *
     * The source is the named policy's highest-numbered version, draft or published.
     */
    copyCommandRolesFromPolicyId: objectIdSchema.optional(),
    commandRoles: policyVersionContentSchema.shape.commandRoles.default([]),
    postTypes: policyVersionContentSchema.shape.postTypes.default([]),
    vehicleTypes: policyVersionContentSchema.shape.vehicleTypes.default([]),
    effectiveFrom: z.iso.datetime().optional(),
  })
  .refine((value) => !(value.copyCommandRolesFromPolicyId && value.commandRoles.length > 0), {
    message: 'Give command roles or a policy to copy them from, not both',
  });

export const updatePolicySchema = policyBaseSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: 'At least one field must be provided' });

export const policySchema = policyBaseSchema.extend({
  id: objectIdSchema,
  /**
   * ⚠️ EXACTLY ONE POLICY CARRIES THIS. Setting it clears the previous, atomically —
   * and there is deliberately no endpoint that CLEARS it without setting another, which
   * is the only thing making "at least one" hold. See `policy.model.ts`.
   */
  isDefault: z.boolean(),
  /**
   * ⚠️ AN ARCHIVED POLICY IS NOT OFFERED FOR NEW ARRANGEMENTS, AND CHANGES NOTHING ELSE.
   * Existing arrangements snapshot a `policyVersionId` and render from it, so they keep
   * working — which is why archiving a policy needs no reference guard at all.
   */
  ...archivableFieldsSchema.shape,
  /** `PUBLISHED` once this policy has ever had a published version. */
  archivedFromStatus: z.enum(MASTER_LIFECYCLE_STATUSES).nullable(),
  /**
   * The latest PUBLISHED version, denormalised. Written by exactly one writer — the
   * publish transaction — so it cannot drift. It exists because "which version does a
   * new PA use?" is asked on every P3 intake and on every row of the policy list, and
   * answering it per row otherwise means an aggregation or an N+1.
   */
  currentVersionId: objectIdSchema.nullable(),
  currentVersionNumber: z.number().int().min(1).nullable(),
  /** The open draft, if any. At most one per policy — enforced by a partial unique index. */
  draftVersionId: objectIdSchema.nullable(),
  draftVersionNumber: z.number().int().min(1).nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

/** The policy list row. `postTypeCount` is computed server-side; see `policy.service.ts`. */
export const policyListItemSchema = policySchema.extend({
  postTypeCount: z.number().int().min(0),
  /**
   * ⚠️ [amended D4.3] WHAT THIS POLICY ASKS FOR, WITHOUT OPENING IT.
   *
   * The client's ask was "see the requirement grid from the list". A policy holds no NUMBERS
   * — only the column SET — so rendering an empty table would be worse than the count it
   * replaced. The columns are the honest answer, and these are them: the current published
   * version's post types then its vehicle types, in the version's own `displayOrder`.
   *
   * ⚠️ ARCHIVED ROWS ARE INCLUDED, AND THAT IS [FIX-1] RATHER THAN AN OVERSIGHT. Filtering
   * here would make the list disagree with the policy screen, and it would hide the one fact
   * an operator most needs — that a column they are still using has been retired. The
   * summaries carry `archivedAt`, so the WEB decides usability, in the one place that already
   * does (`lib/policyColumns.ts`), and discloses rather than hides: "4 posts · 1 retired".
   *
   * ⚠️ AND NO COMMAND ROLES. FR-5.2.6b keeps command out of anything that reads as a
   * requirement column, and a list cell full of chips is exactly that. `lib/policyPreview.ts`
   * makes the same guarantee by SIGNATURE; this makes it by absence.
   */
  postTypes: z.array(postTypeSummarySchema),
  vehicleTypes: z.array(vehicleTypeSummarySchema),
});

export const policySummarySchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  isDefault: z.boolean(),
});

// ── Policy version ───────────────────────────────────────────────────────────────

export const policyVersionSchema = z.object({
  id: objectIdSchema,
  policyId: objectIdSchema,
  versionNumber: z.number().int().min(1),
  status: z.enum(POLICY_VERSION_STATUSES),
  /** ⚠️ [amended D4] `ARRANGEMENT` means "forked from a duty point, never the current one". */
  origin: z.enum(POLICY_VERSION_ORIGINS),
  effectiveFrom: z.iso.datetime().optional(),
  /** Ordered. `displayOrder` is the index, restated so a client cannot get it wrong. */
  postTypes: z.array(
    z.object({
      postTypeId: objectIdSchema,
      displayOrder: z.number().int().min(0),
      /** `null` when the referenced row has been deleted outright, which should not happen. */
      postType: postTypeSummarySchema.nullable(),
    }),
  ),
  vehicleTypes: z.array(
    z.object({
      vehicleTypeId: objectIdSchema,
      displayOrder: z.number().int().min(0),
      vehicleType: vehicleTypeSummarySchema.nullable(),
    }),
  ),
  commandRoles: z.array(commandRoleSchema),
  publishedAt: z.iso.datetime().optional(),
  publishedByOfficerId: objectIdSchema.optional(),
  /**
   * ⚠️ [amended PRE-P6 §3b] THE PUBLISHER'S NAME, BECAUSE A VERSION HISTORY THAT PRINTS AN
   * ObjectId IS NOT A HISTORY.
   *
   * `publishedByOfficerId` above is the reference; this is what a human reads. Divergence
   * #84's rule — no label may fall through to an id — is why the id alone was not enough for
   * `pages/PolicyBuilder`'s history panel, which is this field's one consumer.
   *
   * ⚠️ REQUIRED KEY, NULLABLE VALUE, not `optional()`. CLAUDE.md's shape for exactly this:
   * `null` is a real answer the panel renders ("—") for a version published before there was
   * an actor to record, or whose officer row has since gone; `optional` would drift behind
   * the one builder instead of breaking it. There is exactly ONE builder,
   * `toPolicyVersionDto` — check that is still true before adding a second field.
   *
   * ⚠️ A NAME AND NOT AN `officerSummarySchema`. That carries a phone number, and a policy
   * page has no use for one — §10's minimisation, and the reason this is a string.
   */
  publishedByName: z.string().nullable(),
  createdByOfficerId: objectIdSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

/** `POST /policies` returns both, because the web immediately edits the draft it just made. */
export const policyWithVersionSchema = z.object({
  policy: policySchema,
  version: policyVersionSchema,
});

export const policyVersionParamsSchema = z.object({
  id: objectIdSchema,
  versionId: objectIdSchema,
});

export const POLICY_SORT_FIELDS = ['name', 'updatedAt'] as const;
export type PolicySortField = (typeof POLICY_SORT_FIELDS)[number];

export const listPoliciesQuerySchema = createPaginationSchema(POLICY_SORT_FIELDS, 'name')
  .extend({
    search: z.string().trim().min(1).max(160).optional(),
    /** ⚠️ [LIFECYCLE] Replaced `includeInactive`, and it is not a rename — see `postType.schema.ts`. */
    show: showLifecycleSchema,
  })
  .extend({ sortOrder: z.enum(['asc', 'desc']).default('asc') });

export const POLICY_VERSION_SORT_FIELDS = ['versionNumber'] as const;
export type PolicyVersionSortField = (typeof POLICY_VERSION_SORT_FIELDS)[number];

export const listPolicyVersionsQuerySchema = createPaginationSchema(POLICY_VERSION_SORT_FIELDS, 'versionNumber').extend(
  { status: z.enum(POLICY_VERSION_STATUSES).optional() },
);
